// Clientes Perfil - Información personal del cliente
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/providers/AuthProvider';
import { clienteService } from '@/services/clienteService';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export function ClientesPerfil() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation('portal');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone_number: user?.phone_number || '',
  });

  const hasChanges = formData.full_name !== (user?.full_name || '') ||
    formData.phone_number !== (user?.phone_number || '');

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!formData.full_name.trim()) {
      setSaveError(t('profile.nameRequired', 'El nombre es obligatorio.'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await clienteService.updatePerfil(formData);
      await refreshUser();
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch {
      setSaveError(t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  }, [formData, refreshUser, t, saving]);

  return (
    <ClientLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('profile.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('profile.subtitle')}
          </p>
        </div>

        {/* Feedback */}
        {saveSuccess && (
          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 flex items-center gap-2" role="status">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true" />
            <p className="text-sm text-green-700 dark:text-green-300">{t('profile.updateSuccess')}</p>
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 flex items-center gap-2" role="alert">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
            <p className="text-sm text-red-700 dark:text-red-300">{saveError}</p>
          </div>
        )}

        {/* Información personal */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('profile.personalInfo')}</CardTitle>
              {!editing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  {t('common.edit')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email">{t('profile.email')}</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="mt-1 bg-gray-50 dark:bg-gray-800"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('profile.emailReadonly')}
              </p>
            </div>

            <div>
              <Label htmlFor="full_name">{t('profile.fullName')}</Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => {
                  setFormData({ ...formData, full_name: e.target.value });
                  setSaveError(null);
                }}
                disabled={!editing}
                className={`mt-1 ${!editing ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
              />
            </div>

            <div>
              <Label htmlFor="phone_number">{t('profile.phone')}</Label>
              <Input
                id="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={(e) => {
                  setFormData({ ...formData, phone_number: e.target.value });
                  setSaveError(null);
                }}
                disabled={!editing}
                className={`mt-1 ${!editing ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                placeholder={t('profile.phonePlaceholder')}
                autoComplete="tel"
              />
            </div>

            {editing && (
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? t('common.saving', 'Guardando...') : t('common.save')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFormData({
                      full_name: user?.full_name || '',
                      phone_number: user?.phone_number || '',
                    });
                    setEditing(false);
                    setSaveError(null);
                  }}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Información de cuenta */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.accountInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('profile.role')}</span>
                <div className="text-gray-900 dark:text-gray-100 font-medium capitalize">
                  {user?.role === 'client' ? t('profile.roleClient') : user?.role || 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('profile.status')}</span>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {user?.is_active ? t('profile.statusActive') : t('profile.statusInactive')}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('profile.emailVerified')}</span>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {user?.is_verified ? t('profile.verified') : t('profile.notVerified')}
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('profile.lastAccess')}</span>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {user?.last_login
                    ? new Date(user.last_login).toLocaleDateString('es-ES')
                    : 'N/A'
                  }
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seguridad */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.security')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('profile.securityDesc')}
            </p>
            <Button variant="outline" size="sm" disabled>
              {t('profile.changePassword')}
            </Button>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('profile.comingSoon')}
            </p>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
